import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { PromptTemplateScope } from '../../../../../../backend/src/prompt-template/dto/prompt-template.constants';
import { PromptTemplateResponseDto } from '../../../../../../backend/src/prompt-template/dto/prompt-template.response.dto';
import { useDeletePromptTemplate } from '@/api/mutations/useDeletePromptTemplate';
import { useGetPromptTemplates } from '@/api/queries/useGetPromptTemplates';
import { useHasPermission } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsListHeader,
  SettingsListItem,
  SettingsRow,
  SettingsSection,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import {
  EditableTemplate,
  TemplateBuilderDialog,
} from '@/new/pages/matters/_matterId/subsets/_subsetId/new-kim/template-builder/template-builder-dialog';
import { renderTemplateBody } from '@/new/pages/matters/_matterId/subsets/_subsetId/new-kim/templates/template-list';

export function Templates() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useHasPermission();
  const canManageTemplates = hasPermission(Permission.PromptTemplateManage);
  const { data: templates = [], isLoading } = useGetPromptTemplates({ scope: PromptTemplateScope.Organisation });
  const { mutate: deleteTemplate, isLoading: isDeleting } = useDeletePromptTemplate(
    undefined,
    PromptTemplateScope.Organisation,
  );

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EditableTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<PromptTemplateResponseDto | null>(null);

  const openEditDialog = (template: PromptTemplateResponseDto) => {
    setEditingTemplate({ id: template.id, title: template.title, body: template.body });
  };

  const handleConfirmDelete = () => {
    if (!deletingTemplate) return;
    deleteTemplate(
      { id: deletingTemplate.id },
      {
        onSuccess: () => setDeletingTemplate(null),
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Error deleting template. Please try again.');
        },
      },
    );
  };

  const countLabel =
    templates.length === 0 ? 'No templates added' : `${templates.length} template${templates.length === 1 ? '' : 's'}`;

  if (!canManageTemplates) {
    return (
      <SettingsContent>
        <SettingsSection title="Templates">
          <SettingsRow
            title="Permission required"
            description={`You do not have permission to manage ${t('organisation')} templates. Please contact your administrator.`}
          />
        </SettingsSection>
      </SettingsContent>
    );
  }

  return (
    <SettingsContent>
      <SettingsSection
        title="Templates"
        description={`Reusable Kim prompt templates shared with everyone in your ${t('organisation')}. They appear in the ${t('Organisation')} tab of Kim's template picker.`}
      >
        <SettingsListHeader
          label={isLoading ? 'Loading templates...' : countLabel}
          onAdd={() => setIsBuilderOpen(true)}
          addLabel="Add template"
          addDisabled={isLoading}
        />

        {templates.map((template) => (
          <SettingsListItem
            key={template.id}
            onClick={() => openEditDialog(template)}
            title={template.title}
            description={
              <>
                <span className="block truncate">{renderTemplateBody(template.body)}</span>
                <span className="mt-0.5 block truncate text-xs text-gray-400">
                  Created{template.createdBy?.email ? ` by ${template.createdBy.email}` : ''} on{' '}
                  {new Date(template.createdDate).toLocaleDateString(i18n.language, { dateStyle: 'long' })}
                </span>
              </>
            }
            actions={
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="iconSm" aria-label="Template options" className="text-gray-400">
                    <Icon className="lucide--ellipsis size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(template);
                    }}
                  >
                    <Icon className="lucide--pencil" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingTemplate(template);
                    }}
                    className="text-red-600 focus:bg-red-50 focus:text-red-700 [&>svg]:text-red-600"
                  >
                    <Icon className="lucide--trash-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        ))}
      </SettingsSection>

      <TemplateBuilderDialog
        scope={PromptTemplateScope.Organisation}
        open={isBuilderOpen || editingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsBuilderOpen(false);
            setEditingTemplate(null);
          } else {
            setIsBuilderOpen(true);
          }
        }}
        template={editingTemplate ?? undefined}
      />

      <ConfirmationModal
        isOpen={deletingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingTemplate(null);
        }}
        title="Delete template"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete <strong>{deletingTemplate?.title}</strong> for everyone in your{' '}
        {t('organisation')}? This cannot be undone.
      </ConfirmationModal>
    </SettingsContent>
  );
}
